import type { MenuItem } from "@/gql/gen";
import { computed, onBeforeUnmount, onMounted, onUpdated, Ref, toValue } from 'vue';
import { JsonViewerProps } from "vue3-json-viewer/dist/Components/json-viewer.vue";
import { useDebugStore } from '../store/debug';


type Actions = Ref<Partial<MenuItem>[]> | Partial<MenuItem>[]

type Field = {
  label?: string
  value?: Ref<any> | any
  json?: boolean
  jsonParams?: JsonViewerProps
  width?: string
}

type Fields = Ref<Field[]> | Field[]


export type DebugComponentModel = {
  actions: Ref<MenuItem[]>,
  fields: Ref<Field[]>,
  debugEnable: Ref<boolean>,
}

export type DebugComponentProps = {
  data: DebugComponentModel,
}

interface DebugOptions {
  collectRenders?: boolean
  logProps?: boolean
  logLifecycle?: boolean
  actions?: Actions | (() => Actions)
  fields?: Fields | (() => Fields)
}

/**
 * Композабл для отладки компонентов
 * Собирает статистику ререндеров и другую отладочную информацию
 *
 * @param componentName - Имя компонента для идентификации
 * @param options - Опции отладки
 */
export function useDebugComponent(
  componentName: string,
  options: DebugOptions = {}
) {

  const {
    actions: _actions = [],
    fields: _fields = [],
    collectRenders = false,
    logProps = false,
    logLifecycle = false
  } = options

  const debugStore = useDebugStore()
  const debugEnable = computed(() => debugStore.debugOptions.componentsDebug)

  const actions = computed(() => {
    if (!debugEnable.value)
      return []
    const items = typeof _actions === "function" ? _actions() : _actions
    return toValue(items) as MenuItem[]
  })

  const fields = computed(() => {
    if (!debugEnable.value)
      return []
    const items = typeof _fields === "function" ? _fields() : _fields
    return toValue(items)
  })


  // Счетчик ререндеров для текущего экземпляра
  let renderCount = 0

  // Отслеживание монтирования (первый рендер)
  if (collectRenders) {
    onMounted(() => {
      renderCount++
      debugStore.incrementRender(componentName)

      if (logLifecycle || debugStore.isComponentDebugEnabled) {
        console.log(`🔄 [${componentName}] Initial Render #${renderCount}`)
      }
    })

    // Отслеживание последующих ререндеров через onUpdated
    onUpdated(() => {
      renderCount++
      debugStore.incrementRender(componentName)

      if (logLifecycle || debugStore.isComponentDebugEnabled) {
        console.log(`🔄 [${componentName}] Re-render #${renderCount}`)
      }
    })
  }

  // Логирование жизненного цикла
  if (logLifecycle) {
    onMounted(() => {
      console.log(`🟢 [${componentName}] Mounted`)
    })

    onBeforeUnmount(() => {
      console.log(`🔴 [${componentName}] Unmounting (${renderCount} renders total)`)
    })
  }

  // Вспомогательные функции для логирования
  const logRender = (message?: string) => {
    if (debugStore.isComponentDebugEnabled) {
      console.log(`🔄 [${componentName}] ${message || 'Manual render log'}`)
    }
  }

  const logAction = (action: string, data?: any) => {
    if (debugStore.isComponentDebugEnabled) {
      console.log(`⚡ [${componentName}] Action: ${action}`, data)
    }
  }

  const logError = (error: string | Error, context?: any) => {
    if (debugStore.isComponentDebugEnabled) {
      console.error(`❌ [${componentName}] Error:`, error, context)
    }
  }

  return {
    logRender,
    logAction,
    logError,
    get renderCount() { return renderCount },
    debugEnable,
    actions,
    fields,
  }
}
